import _ from "lodash";
import PropTypes from "prop-types";
import React from "react";
import classes from "./RadioButtonList.scss";
import LinkWithTooltip from "UIComponents/LinkWithTooltip";
import RadioButton from "UIComponents/RadioButton";
import UIBaseComponent from "UIComponents/UIBaseComponent";
import classNames from "classnames";
import { CardSelection, Tooltip } from "@toddle-edu/ds-web";
import { InfoIcon } from "SvgComponents";
import { colors } from "Constants";
import { InformationOutlined } from "@toddle-edu/ds-icons";
import ConditionalWrapper from "Utils/ConditionalWrapper";

class RadioButtonList extends UIBaseComponent {
  constructor(props) {
    super(props);
  }

  getAllOption = () => {
    const {
      listItemStyle,
      options,
      value,
      disabled,
      itemSelectedStyle,
      buttonColor,
      name,
      labelStyle,
      dsVersion,
      size,
    } = this.props;

    let allOptionLabelLocale = this.props.allOptionLabel;

    if (allOptionLabelLocale === "common:all") {
      allOptionLabelLocale = this.props.t(allOptionLabelLocale);
    }

    const optionValues = _.map(options, ({ value }) => value);

    const isChecked = _.size(value) === _.size(optionValues);

    return (
      <div
        className={classes.option}
        style={{
          ...listItemStyle,
          ...(isChecked ? itemSelectedStyle : {}),
        }}
        onClick={disabled ? null : () => this.updateValue(optionValues)}
      >
        <RadioButton
          label={allOptionLabelLocale}
          isChecked={isChecked}
          isDisabled={disabled}
          color={buttonColor}
          labelStyles={labelStyle}
          name={name}
          dsVersion={dsVersion}
          size={size}
        />
      </div>
    );
  };

  getOption = () => {
    const {
      listItemStyle,
      options,
      disabled,
      value,
      buttonColor,
      labelStyle,
      itemSelectedStyle,
      name,
      isOptionWithBorder,
      displayDisabledAsActive,
      testId,
      dsVersion,
      size,
    } = this.props;

    /*******************************************************************************************************
     * displayDisabledAsActive flag is used to control the visual state of this component,
     * where if it is true, we will show the element in visually active form but
     * functionality wise it will behave like disabled.
     ********************************************************************************************************/

    const listItem = classNames(
      { [classes.option]: true },
      { [classes.optionWithBorder]: isOptionWithBorder },
      { [classes.displayDisabledAsActive]: disabled && displayDisabledAsActive }
    );
    return _.map(options, (option, index) => {
      const { label, value: optionValue, subText } = option;
      return (
        <div
          key={index}
          className={listItem}
          style={{
            ...listItemStyle,
            ...(value === optionValue ? itemSelectedStyle : {}),
          }}
          onClick={disabled ? null : () => this.updateValue(optionValue)}
          data-test-id={`${testId}-radioButtonList-${optionValue}-option`}
        >
          {/*******************************************************************************************************
           * We have modified listItemClass to form a parent child relationship between the DS component
           * and this component, to include styling for displayDisabledAsActive state, so please check the css before
           * adding any wrapper element to the component and check that existing behaviour remains the same
           ********************************************************************************************************/}
          <RadioButton
            label={label}
            subText={subText}
            isChecked={value === optionValue}
            isDisabled={disabled && !displayDisabledAsActive}
            color={buttonColor}
            labelStyles={labelStyle}
            testId={`${testId}-radioButtonList-${optionValue}-option`}
            name={name}
            dsVersion={dsVersion}
            size={size}
          />
        </div>
      );
    });
  };

  getCardSelectionOptions = () => {
    const {
      options,
      listItemStyle,
      disabled,
      value,
      testId,
      dsVersion,
      displayDisabledAsActive,
    } = this.props;

    return _.map(options, option => {
      //optionDisable field  is for when we want to disable particular option not whole option list
      const { label, value: optionValue, subText, optionDisable } = option;
      const suffixIcon = subText ? (
        dsVersion === "2.0" ? (
          <Tooltip
            tooltip={<div className={classes.tooltip}>{subText}</div>}
            dsVersion={dsVersion}
          >
            <InformationOutlined
              size="xx-small"
              tabIndex={0}
              className={classes.focusOutline}
            />
          </Tooltip>
        ) : (
          <LinkWithTooltip
            tooltip={subText}
            placement={"bottom"}
            trigger={["hover"]}
            tooltipContainerStyle={{ width: 284 }}
          >
            <div className={classes.infoIcon}>
              <InfoIcon fill={colors.neutral400} width={14} height={14} />
            </div>
          </LinkWithTooltip>
        )
      ) : null;

      return (
        <ConditionalWrapper
          condition={disabled && displayDisabledAsActive}
          wrapper={<div className={classes.displayDisabledAsActiveCard} />}
          type={"wrapperAsElement"}
        >
          <CardSelection
            title={label}
            type="radio"
            key={optionValue}
            value={optionValue}
            containerStyle={listItemStyle}
            suffixIcon={suffixIcon}
            disabled={
              optionDisable
                ? optionDisable
                : disabled && !displayDisabledAsActive
            }
            selectedValues={[value]}
            onCardSelectionClick={() => this.updateValue(optionValue)}
            testId={`${testId}-radioButtonList-${optionValue}-option`}
            dsVersion={dsVersion}
          />
        </ConditionalWrapper>
      );
    });
  };

  shouldShowEditEmpty = () => {
    return _.get(this.props.options, "length", 0) == 0;
  };

  renderView = () => {
    const { viewTextStyle } = this.props;
    return (
      <div className={classes.viewText} style={viewTextStyle}>
        {this.getViewValue()}
      </div>
    );
  };

  getViewValue = () => {
    const { options, value, emptyText } = this.props;

    return value
      ? _.get(_.find(options, { value: value }), "label", "")
      : emptyText
      ? emptyText
      : "N/A";
  };

  renderEdit = () => {
    const {
      listContainerStyle,
      allOptionLabel,
      testId,
      useCardSelection,
    } = this.props;

    return (
      <div className={classes.container}>
        <div
          className={classes.optionContainer}
          style={listContainerStyle}
          data-test-id={`${testId}-radioButtonList-optionList`}
        >
          {/* {allOptionLabel ? this.getAllOption() : null} */}
          {useCardSelection ? this.getCardSelectionOptions() : this.getOption()}
        </div>
      </div>
    );
  };
}

export default RadioButtonList;

RadioButtonList.defaultProps = {
  ...UIBaseComponent.defaultProps,
  options: [],
  buttonColor: "blue",
  labelStyle: {},
  itemSelectedStyle: {},
  listItemStyle: {},
  viewTextStyle: {},
  useCardSelection: false,
  displayDisabledAsActive: false,
};

RadioButtonList.propTypes = {
  ...UIBaseComponent.propTypes,
  options: PropTypes.array,
  displayDisabledAsActive: PropTypes.bool,
};
